import {
  AloftDate,
  AloftDateJSON,
  CompsListingsSearchCriteria,
  MLSListing,
  PropertyData,
  RecommendedCompsStatistics,
  SubjectData,
  isDefined,
} from '@aloft/toolkit-shared';
import { Block, Button, Flex, Modal } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { useDisclosure } from '@aloft/toolkit-sui/chakra';
import { SortingState } from '@tanstack/react-table';

import { SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useAutofillProperty } from '../../hooks/useAutofillProperty.ts';
import { useToaster } from '../../hooks/useToaster/useToaster.tsx';
import { Tkv2ReferenceViewDrawer } from '../../modules/referenceView/Tkv2ReferenceViewDrawer.tsx';
import { removeComps } from '../../redux/features/actions/removeComp.ts';
import { selectAccount } from '../../redux/features/selectors/accountSelectors.ts';
import { featuresSelectors } from '../../redux/features/selectors/featuresSelectors.ts';
import { selectOrder } from '../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import {
  LocationType,
  formatProximityRequirementTooltip,
  getLocationTypeProximityRequirementMiles,
} from '../../utils/locationTypeProximityRequirementMiles.ts';
import { proximityMiles } from '../../utils/proximityMiles.ts';
import { LoadingScreenSamFull } from '../LoadingScreen/LoadingScreenSamFull.tsx';
import { Tkv2ReferenceBar } from '../ReferenceBar/Tkv2ReferenceBar.tsx';
import { useRecommendedCompsFilterRoute } from './hooks/useRecommendedCompsFilterRoute.ts';
import { useRecommendedCompsInternalState } from './hooks/useRecommendedCompsInternalState.ts';
import { useRecommendedProperties } from './hooks/useRecommendedProperties.ts';
import { CraftsmanRecommendedCompSelection } from './internal/CraftsmanRecommendedCompSelection.tsx';
import { FilterDrawer } from './internal/FilterDrawer/FilterDrawer.tsx';
// eslint-disable-next-line aloft/prefer-sound-ui-button
import { FilterDrawerButton } from './internal/FilterDrawer/FilterDrawerButton.tsx';
import { dismissDefaultFiltersInfo } from './internal/FilterDrawer/defaultFiltersInfoToastStorage.ts';
import { QualityTierBreakdown } from './internal/qualityTier/QualityTierBreakdown.tsx';
import {
  CompSelectionState,
  FiltersMessage,
  InitialCompSearch,
  QualityTierBreakdownData,
  schCompSearch,
} from './types.ts';
import { getZillowCompRecID } from './utils/getZillowCompRecID.ts';
import { handleAddClickForCompCreation } from './utils/handleAddClickForCompCreation.ts';
import { handleAddClickZillowCraftsman } from './utils/handleAddClickZillowCraftsman.ts';
import { loadExistingSearchCriteria } from './utils/loadExistingSearchCriteria.ts';

const { selectShouldShowRecommendedCompsFiltering } = featuresSelectors;

// The modal keeps track of selected comps, and handles actually adding them.
// Returns IDs of comps that are selected as either AIV or ARV (or both) for Inspectify orders with ARV option,
// or just IDs that are selected (old behavior) for non-Inspectify orders or Inspectify orders without ARV option
const getSelectedIDs = (
  rowSelection: CompSelectionState,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
): string[] => {
  if (isInspectifyOrder && hasArvOption) {
    return Object.entries(rowSelection)
      .filter(([, value]) => value.aiv || value.arv)
      .map(([key]) => key);
  }
  // Old behavior: just check if the key exists in rowSelection
  return Object.keys(rowSelection);
};

type CraftsmanProps = {
  createComps: (props: PropertyData[]) => void;
  currentComps: Record<string, PropertyData>;
  compSeq: string[];
  orderID: string;
  isModalOpen: boolean;
  onModalClose: () => void;
  subject: SubjectData;
  effectiveDate: AloftDateJSON;
};

export const CraftsmanRecommendCompsModal = ({
  createComps,
  currentComps,
  compSeq,
  orderID,
  isModalOpen,
  onModalClose,
  subject,
  effectiveDate,
}: CraftsmanProps) => {
  const dispatch = useDispatch();
  const makeToast = useToaster();
  const { isLoading, data, getProperties, isError } = useRecommendedProperties({ orderID });
  const order = useAppSelector((state) => selectOrder(state));
  const { hasArvOption } = order;
  const autofillProperty = useAutofillProperty();
  const [isAddCompsLoading, setIsAddCompsLoading] = useState(false);
  const [newFiltersApplied, setNewFiltersApplied] = useState(false);
  const [filtersMessage, setFiltersMessage] = useState<FiltersMessage | undefined>(undefined);
  const [allowApplyNewFilters, setAllowApplyNewFilters] = useState(false);
  const account = useAppSelector(selectAccount);
  const ref: React.RefObject<{
    downloadCSV: () => void;
    getQualityTiersBreakdown: () => QualityTierBreakdownData;
  } | null> = useRef(null);

  // Keep track of the comps that the user selected, so we can add them when we are ready
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<CompSelectionState>({});
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const previousDataRef = useRef<typeof data>();
  const [qualityTierBreakdownData, setQualityTierBreakdownData] =
    useState<QualityTierBreakdownData>();

  const foundComps = data?.body.status !== undefined;
  let existingSearchCriteria: CompsListingsSearchCriteria | undefined;
  let statistics: RecommendedCompsStatistics | undefined;

  if (data?.body.output?.searchCriteria) {
    existingSearchCriteria = data.body.output.searchCriteria;
  }

  if (
    (data?.source === 'constellation1' || data?.source === 'abode') &&
    data?.body?.status === 'found'
  ) {
    statistics = data.body?.output?.statistics;
  }

  const showButtons = foundComps && !isLoading;

  const featureAllowRecommendedCompsFiltering = useAppSelector((state) =>
    selectShouldShowRecommendedCompsFiltering(state),
  );

  const allowRecommendedCompsFiltering =
    featureAllowRecommendedCompsFiltering || order.isInspectifyOrder;

  const { form, showNoRecommendations, setShowNoRecommendations, getDefaultFormValues } =
    useRecommendedCompsInternalState(subject, AloftDate.schema.parse(effectiveDate));

  const currentCompsSet = useMemo(
    (): Record<string, string> =>
      Object.entries(currentComps)
        .map(([compId, comp]) => {
          const toolkitId = comp.readonlyData.backupListingData?.listing?.ToolkitListingID;
          return toolkitId ? [toolkitId as string, compId] : null;
        })
        .filter((entry): entry is [string, string] => entry !== null)
        .reduce<Record<string, string>>((acc, [toolkitId, compId]) => {
          acc[toolkitId] = compId;
          return acc;
        }, {}),
    [currentComps],
  );

  const { setValue } = form;
  useEffect(() => {
    if (data && !isError && data !== previousDataRef.current) {
      previousDataRef.current = data;

      let search = {} as InitialCompSearch['body']['search'];
      if (existingSearchCriteria) {
        search = loadExistingSearchCriteria(existingSearchCriteria, subject, getDefaultFormValues);
      } else {
        search = getDefaultFormValues();
      }
      setValue('search', search);
      setValue('originalSearch', search);
    }
  }, [data, isError, existingSearchCriteria, subject, getDefaultFormValues, setValue]);

  useEffect(() => {
    if (data && !isError) {
      const currentCompsKeys = Object.keys(currentCompsSet || {});
      if (currentCompsKeys.length > 0 && !hasInitializedSelection) {
        // Reset row selection to only include current comps
        // Only do this once when data first loads, not on every dependency change
        if (order.isInspectifyOrder && hasArvOption) {
          // Initialize with existing comps and their compType from PropertyData
          // Handle case where multiple comps might share the same listingID with different compTypes
          setRowSelection((prevSelection) => {
            const newSelection: CompSelectionState = { ...prevSelection };

            // First, initialize from currentCompsSet (mapping listingID -> compId)
            currentCompsKeys.forEach((listingId) => {
              const compId = currentCompsSet[listingId];
              const comp = compId ? currentComps[compId] : undefined;
              if (comp) {
                // Initialize or update selection for this listingID
                const existing = newSelection[listingId] || {};
                newSelection[listingId] = {
                  aiv: existing.aiv || comp.compType === 'aiv',
                  arv: existing.arv || comp.compType === 'arv',
                };
              }
            });

            // Also check all comps in case there are multiple comps with same listingID but different compTypes
            Object.values(currentComps).forEach((comp) => {
              const listingId =
                comp.readonlyData.backupListingData?.listing?.ToolkitListingID ||
                comp.readonlyData.backupListingData?.listing?.ListingId;
              if (listingId) {
                const existing = newSelection[listingId] || {};
                newSelection[listingId] = {
                  aiv: existing.aiv || comp.compType === 'aiv',
                  arv: existing.arv || comp.compType === 'arv',
                };
              }
            });

            return newSelection;
          });
        } else {
          // Old behavior: just initialize with listing IDs as keys
          // This applies to non-Inspectify orders and Inspectify orders without ARV option
          setRowSelection(() => {
            const newSelection: CompSelectionState = {};
            currentCompsKeys.forEach((listingId) => {
              newSelection[listingId] = {};
            });
            return newSelection;
          });
        }
        setHasInitializedSelection(true);
      }
    }
  }, [
    data,
    isError,
    currentCompsSet,
    currentComps,
    hasInitializedSelection,
    order.isInspectifyOrder,
    hasArvOption,
  ]);

  // Subscribe to form changes to track when search values differ from originalSearch
  useEffect(() => {
    const subscribe = form.subscribe({
      formState: {
        values: true,
      },
      callback: (state) => {
        // Compare the UI search values with the version that was submitted to the backend
        const stateChangedFromOriginal =
          JSON.stringify(state.values.originalSearch) !== JSON.stringify(state.values.search);

        const { geoSearch } = state.values.search;
        const geoSearchValid =
          (geoSearch.t === 'polygon' && geoSearch.coords.length > 0) ||
          (geoSearch.t === 'radius' && geoSearch.radiusInMeters > 0);
        setAllowApplyNewFilters(stateChangedFromOriginal && geoSearchValid);
      },
    });

    return subscribe;
  }, [form]);

  const { isFilterDrawerOpen, onFilterDrawerOpen, onFilterDrawerClose } =
    useRecommendedCompsFilterRoute();

  const {
    isOpen: isQualityTierBreakdownOpen,
    onOpen: onQualityTierBreakdownOpen,
    onClose: onQualityTierBreakdownClose,
  } = useDisclosure();

  const handleFilterDrawerClose = () => {
    onFilterDrawerClose();
    form.setValue('search', form.getValues('originalSearch'));
  };

  const applyFilters = () => {
    setShowNoRecommendations(false);
    getProperties({
      search: schCompSearch.parse(form.getValues('search')),
    });
    setNewFiltersApplied(true);
    setFiltersMessage(undefined);
    setAllowApplyNewFilters(false);
    dismissDefaultFiltersInfo(orderID);
  };

  useEffect(() => {
    if (isLoading) return;

    if (isError || (data && data.body.status === 'error')) {
      setFiltersMessage({
        type: 'error',
        message: 'Failed to load recommended comps. Please try again or update your filters.',
        color: 'alertError',
        icon: 'warning',
      });
    } else if (!newFiltersApplied) {
      setFiltersMessage(undefined);
    } else if (
      data &&
      (data.source === 'abode' || data.source === 'constellation1') &&
      data?.body.status === 'found' &&
      data?.body.output.resultsCount > 100
    ) {
      setFiltersMessage({
        type: 'warning',
        message: 'Too many results, consider narrowing your filters for better focus.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (data && data.body.status === 'not_found') {
      setFiltersMessage({
        type: 'warning',
        message: 'No comps found with the current filters. Try broadening your search criteria.',
        color: 'statusWarning',
        icon: 'warning',
      });
    } else if (newFiltersApplied && !allowApplyNewFilters) {
      setFiltersMessage({
        type: 'success',
        message: 'Filters applied!',
        color: 'alertSuccess',
        icon: 'check',
      });
    } else {
      setFiltersMessage(undefined);
    }
  }, [data, isLoading, newFiltersApplied, isError, allowApplyNewFilters]);

  const handleSetRowSelection = (newSet: SetStateAction<CompSelectionState>) => {
    const resolvedValue = typeof newSet === 'function' ? newSet(rowSelection) : newSet;
    Object.keys(resolvedValue).forEach((key) => {
      const selection = resolvedValue[key];
      if (selection && !selection?.listingData && data?.body.status === 'found') {
        const listing = data.body.output.results.find(
          (result) =>
            ('ToolkitListingID' in result && result.ToolkitListingID === key) ||
            ('ListingId' in result && result.ListingId === key),
        );
        if (listing) {
          selection.listingData = listing as MLSListing;
        }
      }
    });
    setRowSelection(resolvedValue);
  };

  const closeModal = () => {
    setRowSelection({}); // reset row selection
    setHasInitializedSelection(false); // reset initialization flag
    onModalClose();
  };

  const handleAddClick = async () => {
    setIsAddCompsLoading(true);
    try {
      if (data?.body.status !== 'found') {
        console.warn('No recommended comps attachment found');
        return;
      }

      const selectedIDs = getSelectedIDs(rowSelection, order.isInspectifyOrder, hasArvOption); // actual comp IDs
      const compsToAdd = selectedIDs.filter((id) => !currentCompsSet[id] && id !== 'subject');

      const locationType = subject.editableData.propertyIdentification.locationType as
        | LocationType
        | undefined;
      const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
      const subjectCoordinates = subject.editableData.propertyIdentification.coordinates;

      if (
        locationType &&
        proximityRequirementMiles !== undefined &&
        subjectCoordinates &&
        compsToAdd.length > 0
      ) {
        if (data.source === 'constellation1' || data.source === 'abode') {
          const outOfRangeCount = data.body.output.results.filter((listing) => {
            const id = listing.ToolkitListingID || listing.ListingId;
            if (!id || !compsToAdd.includes(id)) return false;
            if (listing.Latitude === undefined || listing.Longitude === undefined) return false;
            const miles = proximityMiles(subjectCoordinates, {
              latitude: listing.Latitude,
              longitude: listing.Longitude,
            });
            return miles !== undefined && miles > proximityRequirementMiles;
          }).length;

          if (outOfRangeCount > 0) {
            makeToast({
              id: `proximity-requirement-exceeded-${orderID}`,
              type: 'warning',
              title: 'Proximity warning',
              body: formatProximityRequirementTooltip(locationType, proximityRequirementMiles),
            });
          }
        }

        if (data.source === 'z') {
          const outOfRangeCount = data.body.output.results.filter((listing) => {
            const id = getZillowCompRecID(listing);
            if (!id || !compsToAdd.includes(id)) return false;
            if (listing.latitude === null || listing.longitude === null) return false;
            const miles = proximityMiles(subjectCoordinates, {
              latitude: listing.latitude,
              longitude: listing.longitude,
            });
            return miles !== undefined && miles > proximityRequirementMiles;
          }).length;

          if (outOfRangeCount > 0) {
            makeToast({
              id: `proximity-requirement-exceeded-${orderID}`,
              type: 'warning',
              title: 'Proximity warning',
              body: formatProximityRequirementTooltip(locationType, proximityRequirementMiles),
            });
          }
        }
      }

      // Build selection state for new comps to add
      const newRowSelection: CompSelectionState = compsToAdd.reduce<CompSelectionState>(
        (acc, id) => {
          acc[id] = rowSelection[id] || {};
          return acc;
        },
        {},
      );

      const compsToDelete = Object.keys(currentCompsSet)
        .filter((id) => !selectedIDs.includes(id) && id !== 'subject')
        .map((id) => currentCompsSet[id])
        .filter(isDefined);

      if (compsToDelete.length > 0) {
        dispatch(removeComps({ ids: compsToDelete }));
      }

      if (data.source === 'constellation1' || data.source === 'abode') {
        await handleAddClickForCompCreation({
          isLoading,
          rowSelection: newRowSelection,
          createComps,
          autofillProperty,
          effectiveDate,
          orderID,
          isInspectifyOrder: order.isInspectifyOrder,
          hasArvOption,
        });
      }

      if (data.source === 'z') {
        await handleAddClickZillowCraftsman({
          data: data.body.output.results,
          isLoading,
          rowSelection: newRowSelection,
          createComps,
          autofillProperty,
          effectiveDate,
          orderID,
          isInspectifyOrder: order.isInspectifyOrder,
          hasArvOption,
        });
      }

      closeModal();
    } catch (e: unknown) {
      console.error(e, 'Failed to add recommended comps.');
      throw e;
    } finally {
      setIsAddCompsLoading(false);
    }
  };

  return (
    <FormProvider {...form}>
      <LoadingScreenSamFull
        isLoading={isAddCompsLoading || (isLoading && !isFilterDrawerOpen)}
        what="comps"
        wrapperProps={{ backgroundColor: 'surface1' }}
      />
      <Modal
        size="full"
        isOpen={isModalOpen}
        onClose={closeModal}
        closeOnEsc
        showCloseButton={false}
        primaryAction={
          showButtons
            ? {
                id: 'add-btn',
                text: 'Update Comps',
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick: handleAddClick,
                __dangerDoNotUseSx: { padding: '0 14px' },
              }
            : undefined
        }
        secondaryAction={
          showButtons
            ? {
                text: 'Cancel',
                onClick: closeModal,
                variant: 'inline',
                marginRight: '1rem',
                __dangerDoNotUseSx: { padding: '0 14px' },
              }
            : undefined
        }
        modalBodyProps={{ paddingRight: 0, overflow: 'visible' }}
        leftFooterContent={
          allowRecommendedCompsFiltering &&
          !isLoading && (
            <Flex marginBottom="20px" gap={3}>
              <FilterDrawerButton onFilterDrawerOpen={onFilterDrawerOpen} />
              {hasArvOption && (
                <Button
                  icon="quality-tiers"
                  size="md"
                  variant="secondary"
                  text="Quality Tiers"
                  __dangerDoNotUseSx={{ padding: '0 14px' }}
                  onClick={() => {
                    setQualityTierBreakdownData(ref.current?.getQualityTiersBreakdown());
                    onQualityTierBreakdownOpen();
                  }}
                />
              )}
              {account.isAdmin && (
                <Button
                  icon="download"
                  size="md"
                  variant="secondary"
                  text="Export Comps to CSV"
                  __dangerDoNotUseSx={{ padding: '0 14px' }}
                  onClick={() => ref.current?.downloadCSV()}
                />
              )}
            </Flex>
          )
        }
      >
        {allowRecommendedCompsFiltering && (
          <FilterDrawer
            isOpen={isFilterDrawerOpen}
            onCancel={handleFilterDrawerClose}
            onApply={applyFilters}
            subject={subject}
            effectiveDate={effectiveDate}
            isLoading={isLoading}
            statistics={statistics}
            filtersMessage={filtersMessage}
            showSaleDateWarning={allowApplyNewFilters || newFiltersApplied}
            allowApplyNewFilters={allowApplyNewFilters}
            orderID={orderID}
          />
        )}

        <QualityTierBreakdown
          isOpen={isQualityTierBreakdownOpen}
          onClose={onQualityTierBreakdownClose}
          qualityTierBreakdownData={qualityTierBreakdownData}
        />

        <Block height="calc(100vh - 120px)" marginTop="-20px">
          <Flex height="100%" width="100%">
            <Tkv2ReferenceViewDrawer>
              <CraftsmanRecommendedCompSelection
                orderID={orderID}
                subject={subject}
                comps={currentComps}
                compSeq={compSeq}
                effectiveDate={effectiveDate}
                sorting={sorting}
                setSorting={setSorting}
                rowSelection={rowSelection}
                setRowSelection={handleSetRowSelection}
                currentComps={currentCompsSet}
                showNoRecommendations={showNoRecommendations}
                setShowNoRecommendations={setShowNoRecommendations}
                getDefaultFormValues={getDefaultFormValues}
                isInspectifyOrder={order.isInspectifyOrder}
                hasArvOption={hasArvOption}
                allowRecommendedCompsFiltering={allowRecommendedCompsFiltering}
                ref={ref}
              />
            </Tkv2ReferenceViewDrawer>
            <Block marginTop="-20px">
              <Tkv2ReferenceBar />
            </Block>
          </Flex>
        </Block>
      </Modal>
    </FormProvider>
  );
};
